"use client";

import { createContext, type ReactNode, useContext } from "react";
import type { AppLocale } from "../i18n/messages";

/** Looks up a message by its full key, such as `auth.title`, with ICU values. */
export type Translate = (key: string, values?: Record<string, string | number>) => string;

interface TextValue {
  readonly t: Translate;
  readonly locale: AppLocale;
}

const TextContext = createContext<TextValue | null>(null);

/**
 * Feature screens take their copy through this provider, so packages/ui stays free of next-intl and use-intl:
 * web passes next-intl's translator, desktop and admin pass use-intl's.
 */
export function TextProvider({
  t,
  locale,
  children,
}: {
  t: Translate;
  locale: AppLocale;
  children: ReactNode;
}) {
  return <TextContext.Provider value={{ t, locale }}>{children}</TextContext.Provider>;
}

export function useText(): TextValue {
  const value = useContext(TextContext);
  if (value === null) throw new Error("useText needs a TextProvider");
  return value;
}
